import { ENEMIES } from '../../data/enemies';
import { GRAVITY, GROUND_Y, SIM_HZ } from '../../data/tuning';
import { createCircle, type Circle } from '../collision';

/**
 * EN-43 Túnel con trampilla (GDD §8.3, issue #129). A trapdoor in the ground opens and launches a barrel straight
 * up to an apex, announced with dust 1.0 s ahead (GDD §8.1, pillar 2). The barrel rises and falls back into the
 * tunnel; dust and the open trapdoor never harm (the ground is already lethal, §5.2). It is reusable by JEFE-4
 * (#133) through `fire()`. Pure and deterministic: the barrel's height is a parabola of the ticks since launch.
 */

export const EN43 = ENEMIES['EN-43'];

export const DUST_TICKS = Math.round(EN43.dustLeadSeconds * SIM_HZ);
/** Vertical speed leaving the trapdoor, from the apex height (like EN-11, #87). */
export const LAUNCH_SPEED = Math.sqrt(2 * GRAVITY * (EN43.apexY - GROUND_Y));
/** Ticks to the apex (about 41 at 60 Hz). */
export const APEX_TICKS = Math.round((LAUNCH_SPEED / GRAVITY) * SIM_HZ);

export type TunnelPhase = 'closed' | 'dust' | 'launched';
export type LaunchState = 'rising' | 'falling';

export interface TunnelState {
  phase: TunnelPhase;
  /** Ticks since the launch, while the barrel is in flight. */
  flight: number;
  /** Whether the barrel is still rising (for the render). */
  launch: LaunchState;
  /** Whether it has already fired for the current pass of the plane (one barrel per pass). */
  firedForPass: boolean;
}

export const createTunnel = (): TunnelState => ({ phase: 'closed', flight: 0, launch: 'rising', firedForPass: false });

/** Arms the tunnel again (respawn, §6.2 rule 5). */
export function resetTunnel(state: TunnelState): void {
  state.phase = 'closed';
  state.flight = 0;
  state.launch = 'rising';
  state.firedForPass = false;
}

/**
 * The plane came within `triggerDistance` u of the tunnel: start the dust warning. Returns true when the warning
 * just started (so the runner emits the dust event). One warning per pass.
 */
export function trigger(state: TunnelState, distance: number): boolean {
  if (state.phase !== 'closed' || state.firedForPass) return false;
  if (distance > EN43.triggerDistance) return false;
  state.phase = 'dust';
  state.firedForPass = true;
  return true;
}

/** The dust is over: launch the barrel (reusable by JEFE-4). Returns true on the launch tick. */
export function fire(state: TunnelState): boolean {
  if (state.phase !== 'dust') return false;
  state.phase = 'launched';
  state.flight = 0;
  state.launch = 'rising';
  return true;
}

/** One tick of the barrel in flight; it returns to the trapdoor when it falls back. Returns true when it closed. */
export function stepTunnel(state: TunnelState): boolean {
  if (state.phase !== 'launched') return false;
  state.flight += 1;
  if (barrelY(state.flight) <= GROUND_Y) {
    state.phase = 'closed';
    state.flight = 0;
    return true;
  }
  state.launch = state.flight <= APEX_TICKS ? 'rising' : 'falling';
  return false;
}

/** Height of the barrel `flight` ticks after the launch: a parabola from the ground. */
export function barrelY(flight: number): number {
  const t = flight / SIM_HZ;
  return GROUND_Y + LAUNCH_SPEED * t - 0.5 * GRAVITY * t * t;
}

/** Height of the barrel at its apex, for the tests (must equal EN43.apexY). */
export const apexHeight = (): number => barrelY(APEX_TICKS);

/** The barrel body: a circle of `hitboxRadius` u (70-80 % of the model, GDD §8.1). */
export const barrelCircle = (x: number, y: number): Circle => createCircle(x, y, EN43.hitboxRadius);
